/**
 * Focal point coordinates (typically from Directus)
 * Values are percentages (0-100) or decimals (0-1)
 */
export interface FocalPoint {
  /**
   * Horizontal focal point position
   * - 0 = left edge
   * - 50 = center
   * - 100 = right edge
   */
  x: number;

  /**
   * Vertical focal point position
   * - 0 = top edge
   * - 50 = center
   * - 100 = bottom edge
   */
  y: number;
}

/**
 * Convert focal point coordinates to CSS object-position value
 *
 * Handles percentage (0-100), decimal (0-1), and pixel (requires dimensions) formats
 *
 * @param focalPoint - Focal point coordinates
 * @param dimensions - Optional original image dimensions to handle pixel-based coordinates
 * @returns CSS object-position value (e.g., "75% 25%")
 */
export function focalPointToPosition(
  focalPoint: FocalPoint,
  dimensions?: { width: number; height: number }
): string {
  // Check if coordinates exist and are valid numbers
  if (
    focalPoint === null ||
    typeof focalPoint?.x !== "number" ||
    typeof focalPoint?.y !== "number"
  ) {
    return "center";
  }

  // Normalize values to percentage (0-100)
  const normalizeValue = (value: number, total?: number): number => {
    // If total (width/height) is provided, treat value as pixels
    if (total && total > 0) {
      return (value / total) * 100;
    }

    // Directus sometimes sends 0-100 scale. If we get a decimal < 1, it's likely a 0-1 scale.
    if (value > 0 && value < 1) {
      return value * 100;
    }
    return value;
  };

  const x = Math.max(
    0,
    Math.min(100, normalizeValue(focalPoint.x, dimensions?.width))
  );
  const y = Math.max(
    0,
    Math.min(100, normalizeValue(focalPoint.y, dimensions?.height))
  );

  return `${x}% ${y}%`;
}

/**
 * Predefined focal point presets for common use cases
 */
export const FOCAL_POINT_PRESETS = {
  center: { x: 50, y: 50 },
  topLeft: { x: 0, y: 0 },
  topCenter: { x: 50, y: 0 },
  topRight: { x: 100, y: 0 },
  centerLeft: { x: 0, y: 50 },
  centerRight: { x: 100, y: 50 },
  bottomLeft: { x: 0, y: 100 },
  bottomCenter: { x: 50, y: 100 },
  bottomRight: { x: 100, y: 100 },
} as const;
